import countries from 'i18n-iso-countries';
import moment from 'moment';
import * as yup from 'yup';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { MAX_AGE, YEAR_OF_MAJORITY } from 'Components/widgets/contact-info/constants';
import { t } from 'Src/i18n';
import { ENTER } from 'Utils/keyCodes';
export const scrollAndFocusNextInput = (e: KeyboardEvent, dataForm: string) => {
  if (e.which === ENTER) {
    const inputs = document.querySelectorAll(
      `[data-form="${dataForm}"] input, [data-form="${dataForm}"] select`,
    );

    if (inputs) {
      const currentIndex = [...inputs].findIndex(
        (i) => (i as HTMLInputElement).name === (e.target as HTMLInputElement).name,
      );

      if (currentIndex < inputs.length - 1) {
        const selector = (inputs[currentIndex + 1] as HTMLInputElement).name;
        const next = document.querySelector(`[name="${selector}"]`);

        if (next) {
          (next as HTMLElement).focus();
          scrollToElement(`[for="${selector}"]`, 100, undefined, undefined);
        }
      }
    }
  }
};

/* Common Validators */
const nameValidator = (maxlength) =>
  yup
    .string()
    .max(
      maxlength,
      t('common.form.errors.invalidFieldTooLong', {
        maxlength: maxlength,
      }),
    ) // regex to validate first / last name - no special characters, only letters, dashes, dots or apostrophs are allowed
    .matches(
      /^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/,
      t('common.form.errors.noSpecialCharsField'),
    );

export const countryCodeValidator = () =>
  yup
    .string()
    .nullable()
    .min(
      2,
      t('common.form.errors.invalidFieldTooShort', {
        minlength: '2',
      }),
    )
    .max(
      2,
      t('common.form.errors.invalidFieldTooLong', {
        maxlength: '2',
      }),
    )
    .test('countryCode', t('common.form.errors.invalidCountryCode'), (value) =>
      value ? value.match('[A-Za-z]{2}') && countries.isValid(value) : true,
    );
export const firstNameValidator = () => nameValidator(20);
export const lastNameValidator = () => nameValidator(30);
export const unicodeValidator = () => yup.string().max(100);
export const emailNameValidator = () =>
  yup.string().email(t('common.form.errors.invalidEmailField'));
export const emailUniqueValidator = (forbiddenEmails: Array<string>) =>
  yup
    .string()
    .email(t('common.form.errors.invalidEmailField'))
    .notOneOf(forbiddenEmails, t('common.form.errors.invalidEmailEqualCompanion'));
export const birthdateAdultValidator = () =>
  yup
    .date()
    .transform((_value, initialValue) => {
      return initialValue ? new Date(initialValue) : initialValue;
    })
    .typeError(t('common.form.errors.invalidDateField'))
    .max(moment().subtract(YEAR_OF_MAJORITY, 'y'), t('common.form.errors.minimumAgeRequired'))
    .min(moment().subtract(MAX_AGE, 'y'), t('common.form.errors.maximumAgeRequired'));
export const birthdateKidValidator = () =>
  yup
    .date()
    .transform((_value, initialValue) => {
      return initialValue ? new Date(initialValue) : initialValue;
    })
    .typeError(t('common.form.errors.invalidDateField'))
    .max(moment(), t('common.participants.kidsAgeLimit'))
    .min(moment().subtract(YEAR_OF_MAJORITY, 'y'), t('common.participants.kidsAgeLimit'));
export const mobilePhoneNumberValidator = () =>
  yup
    .string()
    .min(
      5,
      t('common.form.errors.invalidFieldTooShort', {
        minlength: 5,
      }),
    )
    .max(
      20,
      t('common.form.errors.invalidFieldTooLong', {
        maxlength: 20,
      }),
    )
    .matches(/^ *\+? *(?:[ \-()]*[0-9][ \-()]*){5,20}$/, t('common.form.errors.phoneNumberError'));
export const passportIdValidator = () =>
  yup
    .string()
    .transform((_value, initialValue) => {
      return initialValue ? initialValue.replace(/ /g, '') : initialValue;
    })
    .min(6, t('common.form.errors.passportMaxMinLength'))
    .max(30, t('common.form.errors.passportMaxMinLength'));
export const passportIssuingDateValidator = () =>
  yup
    .date()
    .nullable()
    .transform((_value, initialValue) => {
      return initialValue ? new Date(initialValue) : initialValue;
    })
    .typeError(t('common.form.errors.invalidDateField'))
    .max(moment(), t('common.form.errors.invalidIssuingDate'));
export const passportExpirationDateValidator = () =>
  yup
    .date()
    .nullable()
    .transform((_value, initialValue) => {
      return initialValue ? new Date(initialValue) : initialValue;
    })
    .typeError(t('common.form.errors.invalidDateField'))
    .min(moment().add(1, 'd'), t('common.form.errors.invalidExpirationDate'));
